"use client"

import type React from "react"
import Link from "next/link"
import Image from "next/image"
import { useState, useEffect } from "react"
import {
  Menu,
  X,
  Phone,
  Calendar,
  Home,
  Settings,
  Info,
  Mail,
  BookOpen,
  HelpCircle,
  MapPin,
  MessageSquare,
} from "lucide-react"
import { usePathname } from "next/navigation"
import { cn } from "@/lib/utils"
import { useModal } from "@/context/modal-context"
import { Button } from "@/components/ui/button"
import TextUsModal from "@/components/text-us-modal"

// Navigation items with icons
const navigation = [
  { name: "Home", href: "/", icon: Home },
  { name: "Services", href: "/services", icon: Settings },
  { name: "Service Areas", href: "/service-areas", icon: MapPin },
  { name: "Blog", href: "/blog", icon: BookOpen },
  { name: "About Us", href: "/about-us", icon: Info },
  { name: "FAQ", href: "/faq", icon: HelpCircle },
  { name: "Contact", href: "/contact", icon: Mail },
]

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [textUsModalOpen, setTextUsModalOpen] = useState(false)
  const pathname = usePathname()
  const [scrolled, setScrolled] = useState(false)
  const { openRequestForm } = useModal()

  // Handle scroll effect (rAF-throttled, passive, and guarded so we only
  // re-render the navbar when the scrolled state actually flips)
  useEffect(() => {
    let ticking = false
    const handleScroll = () => {
      if (ticking) return
      ticking = true
      window.requestAnimationFrame(() => {
        setScrolled((prev) => {
          const next = window.scrollY > 20
          return prev === next ? prev : next
        })
        ticking = false
      })
    }
    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  const handleTextUsClick = (e: React.MouseEvent) => {
    e.preventDefault()
    setTextUsModalOpen(true)
  }

  const handleTextUsContinue = () => {
    // Open SMS without prefilled text
    window.location.href = "sms:+17744200347"
  }

  return (
    <>
      <header
        className={cn(
          "z-40 w-full transition-all duration-300",
          scrolled
            ? "bg-white shadow-md"
            : "bg-gradient-to-r from-gray-50 via-white to-gray-50 border-b border-gray-100",
        )}
      >
        <div className="relative">
          {/* Decorative elements - TOP PANEL DESIGN */}
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-500 via-red-600 to-red-500"></div>
          <div className="absolute top-1 left-0 w-full h-[1px] bg-red-400 opacity-30"></div>

          {/* Additional decorative elements for enhanced top panel */}
          <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-red-600 via-red-500 to-red-600 opacity-20"></div>
          <div className="absolute top-2 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-red-300 to-transparent opacity-50"></div>
        </div>

        <div className="mx-auto max-w-7xl">
          {/* Desktop Navigation */}
          <div className="hidden lg:block">
            <div className="flex items-center justify-between px-4 py-3">
              {/* Logo - left-aligned on desktop, sized to fit nav height */}
              <div className="flex-shrink-0 flex items-center h-[145.9px]">
                <Link href="/" className="block nav-logo h-full py-2">
                  <Image
                    src="/images/appliance-pro-care-logo.png"
                    alt="Appliance Pro Care Logo"
                    width={145}
                    height={145}
                    priority
                    sizes="(max-width: 640px) 94px, 145px"
                    style={{ height: "100%", width: "auto", objectFit: "contain" }}
                  />
                </Link>
              </div>

              {/* Main Navigation and Actions */}
              <div className="flex items-center space-x-8">
                {/* Navigation Links */}
                <nav className="flex items-center space-x-5">
                  {navigation.map((item) => (
                    <Link
                      key={item.name}
                      href={item.href}
                      className={cn(
                        "main-nav-item relative px-3 py-2 text-[15px] font-bold transition-colors duration-200 rounded-md hover:bg-[#e0f0ff]",
                        pathname === item.href ? "text-red-600 bg-gray-50" : "text-gray-800 hover:text-gray-900",
                      )}
                    >
                      <span className="relative z-10">{item.name}</span>
                    </Link>
                  ))}
                </nav>

                {/* Request Service Button and Phone Icon */}
                <div className="flex items-center space-x-4">
                  <Button
                    asChild
                    className="bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 rounded-md shadow-sm hover:shadow-md transition-all duration-200 text-[15px] font-bold hover:scale-[1.02]"
                  >
                    <Link href="/request-service">
                      <span className="flex items-center justify-center">
                        <Calendar className="h-4 w-4 mr-2" />
                        Request Service
                      </span>
                    </Link>
                  </Button>

                  {/* Phone Icon Button */}
                  <a
                    href="tel:+17744200347"
                    className="flex items-center justify-center bg-red-600 hover:bg-red-700 text-white px-4 py-2.5 rounded-md shadow-sm hover:shadow-md transition-all duration-200 text-[15px] font-bold hover:scale-[1.02]"
                    aria-label="Call us"
                  >
                    <Phone className="h-5 w-5" />
                  </a>

                  {/* Text Icon Button */}
                  <button
                    onClick={handleTextUsClick}
                    className="flex items-center justify-center bg-red-600 hover:bg-red-700 text-white px-4 py-2.5 rounded-md shadow-sm hover:shadow-md transition-all duration-200 text-[15px] font-bold hover:scale-[1.02]"
                    aria-label="Text us"
                  >
                    <MessageSquare className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Tablet Navigation - Optimized for iPad 12.9" in portrait with 20% larger logo */}
          <div className="hidden md:block lg:hidden">
            <div className="flex flex-col py-4 px-4">
              {/* Top row with logo - 20% larger */}
              <div className="flex items-center justify-between mb-3">
                <Link href="/" className="block nav-logo h-[109.4px]">
                  <Image
                    src="/images/appliance-pro-care-logo.png"
                    alt="Appliance Pro Care Logo"
                    width={109}
                    height={109}
                    priority
                    sizes="109px"
                    style={{ height: "100%", width: "auto", objectFit: "contain" }}
                  />
                </Link>
              </div>

              {/* Bottom row with navigation and actions */}
              <div className="flex items-center justify-between">
                {/* Hamburger menu for tablet */}
                <button
                  type="button"
                  className="inline-flex items-center justify-center rounded-md p-2 h-10 w-10 text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors duration-200"
                  onClick={() => setMobileMenuOpen(true)}
                >
                  <span className="sr-only">Open menu</span>
                  <Menu className="h-5 w-5" aria-hidden="true" />
                </button>

                {/* Action buttons - right aligned */}
                <div className="flex items-center space-x-2">
                  {/* Request Service Button */}
                  <Button
                    asChild
                    className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-md shadow-sm hover:shadow-md transition-all duration-200 text-[14px] font-bold hover:scale-[1.02]"
                  >
                    <Link href="/request-service">
                      <span className="flex items-center justify-center">
                        <Calendar className="h-4 w-4 mr-1" />
                        Request Now
                      </span>
                    </Link>
                  </Button>

                  {/* Phone Icon Button */}
                  <a
                    href="tel:+17744200347"
                    className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-md shadow-sm hover:shadow-md transition-all duration-200 text-[14px] font-bold hover:scale-[1.02] flex items-center justify-center"
                    aria-label="Call us"
                  >
                    <Phone className="h-4 w-4" />
                  </a>

                  {/* Text Icon Button */}
                  <button
                    onClick={handleTextUsClick}
                    className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-md shadow-sm hover:shadow-md transition-all duration-200 text-[14px] font-bold hover:scale-[1.02] flex items-center justify-center"
                    aria-label="Text us"
                  >
                    <MessageSquare className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Navigation Bar - 20% larger logo */}
          <div className="flex items-center justify-between py-4 px-3 md:hidden">
            {/* Mobile Logo - 20% larger (from h-14 to h-16.8, rounded to h-17) */}
            <div className="flex items-center h-[77.5px]">
              <Link href="/" className="block nav-logo h-full">
                <Image
                  src="/images/appliance-pro-care-logo.png"
                  alt="Appliance Pro Care Logo"
                  width={78}
                  height={78}
                  priority
                  sizes="78px"
                  style={{ height: "100%", width: "auto", objectFit: "contain" }}
                />
              </Link>
            </div>

            {/* Mobile Actions - Horizontal layout with symmetrical alignment */}
            <div className="flex items-center space-x-2">
              {/* All buttons have the same width and height for symmetry */}
              <a
                href="tel:+17744200347"
                className="rounded-md bg-red-600 hover:bg-red-700 w-11 h-11 flex items-center justify-center text-white shadow-sm hover:shadow-md transition-all duration-200 active:bg-red-800"
                aria-label="Call us"
              >
                <Phone className="h-5 w-5" />
              </a>

              {/* Text icon button */}
              <button
                onClick={handleTextUsClick}
                className="rounded-md bg-red-600 hover:bg-red-700 w-11 h-11 flex items-center justify-center text-white shadow-sm hover:shadow-md transition-all duration-200 active:bg-red-800"
                aria-label="Text us"
              >
                <MessageSquare className="h-5 w-5" />
              </button>

              {/* Request Service button - Updated text to "Request Now" */}
              <Button
                asChild
                className="rounded-md bg-red-600 hover:bg-red-700 px-3 h-11 text-sm font-bold text-white shadow-sm hover:shadow-md transition-all duration-200 active:bg-red-800"
              >
                <Link href="/request-service">
                  <span className="flex items-center">
                    <Calendar className="h-4 w-4 mr-1" />
                    <span className="text-sm">Request Now</span>
                  </span>
                </Link>
              </Button>

              {/* Menu toggle button */}
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-md w-11 h-11 text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors duration-200 active:bg-gray-300"
                onClick={() => setMobileMenuOpen(true)}
              >
                <span className="sr-only">Open main menu</span>
                <Menu className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        {mobileMenuOpen && (
          <div className="lg:hidden">
            <div
              className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm transition-opacity"
              onClick={() => setMobileMenuOpen(false)}
            />
            <div className="fixed inset-y-0 right-0 z-50 w-full max-w-sm overflow-y-auto bg-white px-6 py-4 shadow-xl">
              <div className="flex items-center justify-between">
                <Link href="/" onClick={() => setMobileMenuOpen(false)} className="nav-logo h-[73px]">
                  <Image
                    src="/images/appliance-pro-care-logo.png"
                    alt="Appliance Pro Care Logo"
                    width={73}
                    height={73}
                    sizes="73px"
                    style={{ height: "100%", width: "auto", objectFit: "contain" }}
                  />
                </Link>
                <button
                  type="button"
                  className="inline-flex items-center justify-center rounded-md p-2.5 text-gray-500 hover:bg-gray-100 transition-colors duration-200 active:bg-gray-200"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className="sr-only">Close menu</span>
                  <X className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>

              {/* Mobile Menu Items */}
              <div className="mt-6 space-y-2">
                {navigation.map((item) => {
                  const Icon = item.icon
                  return (
                    <Link
                      key={item.name}
                      href={item.href}
                      className={cn(
                        "mobile-nav-item flex items-center space-x-3 px-4 py-3 rounded-md font-medium text-[17px] transition-all duration-200",
                        pathname === item.href
                          ? "bg-red-50 text-red-600"
                          : "text-gray-700 hover:bg-[#e0f0ff] hover:text-gray-900 active:bg-gray-100",
                      )}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Icon className={cn("h-5 w-5", pathname === item.href ? "text-red-600" : "text-gray-500")} />
                      <span>{item.name}</span>
                    </Link>
                  )
                })}
                <Link
                  href="/request-service"
                  className="mobile-nav-item flex items-center space-x-3 px-4 py-3 rounded-md font-bold text-[17px] bg-red-50 text-red-600 hover:bg-red-100 transition-all duration-200 active:bg-red-200"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Calendar className="h-5 w-5 text-red-600" />
                  <span>Request Service</span>
                </Link>
              </div>

              {/* Mobile Call Button */}
              <div className="mt-8 pt-6 border-t border-gray-200 space-y-4">
                <a
                  href="tel:+17744200347"
                  className="flex items-center justify-center gap-3 w-full rounded-md bg-red-600 hover:bg-red-700 px-4 py-4 text-xl font-bold text-white shadow-sm hover:shadow-md transition-all duration-200 active:bg-red-800"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Phone className="h-6 w-6" />
                  Call Us Now
                </a>
                <button
                  onClick={() => {
                    setMobileMenuOpen(false)
                    setTextUsModalOpen(true)
                  }}
                  className="flex items-center justify-center gap-3 w-full rounded-md bg-red-600 hover:bg-red-700 px-4 py-4 text-xl font-bold text-white shadow-sm hover:shadow-md transition-all duration-200 active:bg-red-800"
                >
                  <MessageSquare className="h-6 w-6" />
                  Text Us Now
                </button>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Text Us Modal */}
      <TextUsModal
        isOpen={textUsModalOpen}
        onClose={() => setTextUsModalOpen(false)}
        onContinue={handleTextUsContinue}
      />
    </>
  )
}
